/** @format */

import React from 'react';
import PropTypes from 'prop-types';

import {Button, Icon, ModalFooter, ModalHeader} from '@sttark/design-system';

export const EditModal = ({
    title,
    subTitle,
    buttonLabels,
    simple,
    addingToCart,
    cta,
    buttonUrls,
    isDesctructive,
    buttonIcons,
    disableButtons,
    warning
}) => {
    const isSimple = simple !== null && simple !== undefined && simple !== '' && !addingToCart;
    const isAddingToCart =
        addingToCart !== null && addingToCart !== undefined && addingToCart !== '' && !simple;

    // if the modal is simple this will render
    const SimpleModal = () => {
        const {body} = simple;
        return (
            <section className="card_content-holder p-4 pb-5">
                <p className="font-body-bold-md mb-2">{subTitle}</p>
                <p className="font-body-sm">{body}</p>
            </section>
        );
    };

    // if addingToCart prop exists this will render
    const AddingToCart = () => {
        const keys = Object.keys(addingToCart);
        return (
            <section className="card_content-holder p-4 pb-5">
                <div className="border-bottom border-darker pb-3">
                    <p className="font-body-bold-lg mb-2">{subTitle}</p>

                    <div
                        className="content-holder_o-details d-flex align-items-center justify-content-start flex-wrap"
                        style={{width: '80%'}}>
                        {/* map only through the values that follow the flex flow */}
                        {keys.map((key, i) => {
                            const isPPI = [
                                'pricePerLabel',
                                'pricePerCarton',
                                'pricePerDesign',
                                'itemSubtotal'
                            ];
                            const prettyKey =
                                key &&
                                key
                                    .split(' ')
                                    .map(w => `${w[0].toLocaleUpperCase()}${w.substring(1)}`);
                            return (
                                !isPPI.includes(key) && (
                                    <div key={i}>
                                        <p className="o-details_item m-0 font-body-bold-sm w-100">
                                            {prettyKey}
                                        </p>
                                        <p className="w-100 m-0">{addingToCart[key]}</p>
                                    </div>
                                )
                            );
                        })}
                    </div>

                    {/* price per label/ carton does not follow flex flow, place them after flex flow */}
                    {addingToCart['pricePerLabel'] && (
                        <p className="card_price-per-item text-end">
                            Price Per Label:
                            <span className="font-body-bold-sm">
                                {' '}
                                ${addingToCart['pricePerLabel']}
                            </span>
                        </p>
                    )}
                    {addingToCart['pricePerCarton'] && (
                        <p className="card_price-per-item text-end">
                            Price Per Carton:
                            <span className="font-body-bold-sm">
                                {' '}
                                ${addingToCart['pricePerCarton']}
                            </span>
                        </p>
                    )}
                    {addingToCart['pricePerDesign'] && (
                        <p className="card_price-per-item text-end">
                            Price Per Design:
                            <span className="font-body-bold-sm">
                                {' '}
                                ${addingToCart['pricePerDesign']}
                            </span>
                        </p>
                    )}
                </div>
                {/* item subtotal does not follow flex flow, place it last */}
                {addingToCart['itemSubtotal'] && (
                    <p className="card_item-subtotal mt-2 text-end font-body-bold-lg">
                        Item Subtotal: ${addingToCart['itemSubtotal']}
                    </p>
                )}
            </section>
        );
    };

    return (
        <div className="edit-modal d-flex align-items-center justify-content-center">
            <div className="edit-modal_card m-auto bg-structure-white">
                {/* header  */}
                <ModalHeader handleClose={cta.handleClose}>{title}</ModalHeader>
                {/* content holder  */}
                {isSimple && <SimpleModal />}
                {isAddingToCart && <AddingToCart />}

                {/* Alert */}
                {warning && (
                    <div className="d-flex align-items-center justify-content-center px-4 mb-3">
                        <div className="me-3">
                            <Icon name="warning" color={ColorValuesYellow} />
                        </div>
                        <p>
                            You still need to{' '}
                            <span onClick={cta.handleUploadArtwork} className="text-link">
                                upload artwork.
                            </span>{' '}
                            You can do it later if you aren’t ready now.
                        </p>
                    </div>
                )}
                {/* footer  */}
                <ModalFooter>
                    <div className="d-flex align-items-center justify-content-end">
                        <div className="me-4">
                            <Button
                                secondary
                                disabled={
                                    disableButtons && disableButtons.length > 0 && disableButtons[0]
                                }
                                label={buttonLabels[0]}
                                url={buttonUrls && buttonUrls[0]}
                                iconRight={buttonIcons && buttonIcons[0]}
                                onClick={cta.handleSecondaryChoice}
                            />
                        </div>
                        <div>
                            <Button
                                label={buttonLabels[1]}
                                disabled={
                                    disableButtons && disableButtons.length > 1 && disableButtons[1]
                                }
                                url={buttonUrls && buttonUrls[1]}
                                iconRight={buttonIcons && buttonIcons[1]}
                                onClick={cta.handlePrimaryChoice}
                                destructive={isDesctructive}
                            />
                        </div>
                    </div>
                </ModalFooter>
            </div>
        </div>
    );
};

EditModal.displayName = 'EditModal';

EditModal.defaultProps = {
    title: 'Add modal title',
    subTitle: 'add subtitle',
    buttonLabels: ['First Option', 'Second Option']
};

EditModal.propTypes = {
    title: PropTypes.string.isRequired,
    subTitle: PropTypes.string.isRequired,
    simple: PropTypes.shape({
        body: PropTypes.string.isRequired
    }),
    buttonLabels: PropTypes.array.isRequired,
    buttonUrls: PropTypes.array,
    warning: PropTypes.bool,
    addingToCart: PropTypes.shape({
        material: PropTypes.string,
        laminate: PropTypes.string,
        quantity: PropTypes.string,
        design: PropTypes.string,
        itemSubtotal: PropTypes.string,
        size: PropTypes.string,
        shape: PropTypes.string,
        color: PropTypes.string,
        style: PropTypes.string,
        pricePerLabel: PropTypes.string,
        pricePerCarton: PropTypes.string
    }),
    cta: PropTypes.shape({
        handlePrimaryChoice: PropTypes.func,
        handleSecondaryChoice: PropTypes.func,
        handleClose: PropTypes.func,
        handleUploadArtwork: PropTypes.func
    })
};
